/**
 * Manager PA Home - Morning Briefing
 */

import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle, Clock, Users, DollarSign, FileText, Loader2, ChevronRight, Mic, TrendingDown, UserX, ShieldAlert, Bell, Sun, Moon, Sparkles, X } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/store/authStore';
import { FormattedChatMessage } from '@/components/chat/ChatRenderer';
import { CountUp } from '@/components/pwa/animations';
import { SkeletonHomePage } from '@/components/pwa/Skeleton';
import apiClient from '@/services/api/client';

const fmtCurrency = (v: number) => `$${Math.abs(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

export default function ManagerHomePage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
  const firstName = user?.firstName || user?.email?.split('@')[0] || 'Manager';
  const [showBriefing, setShowBriefing] = useState(false);
  const [briefingType, setBriefingType] = useState<'morning' | 'eod'>('morning');

  const briefingMutation = useMutation({
    mutationFn: async (type: 'morning' | 'eod') => {
      const r = await apiClient.get(`/pa/briefing/${type}`);
      return r.data.data;
    },
  });

  const handleBriefing = (type: 'morning' | 'eod') => {
    setBriefingType(type);
    setShowBriefing(true);
    briefingMutation.mutate(type);
  };

  const { data: dashboard, isLoading, error: dashError } = useQuery({
    queryKey: ['manager-dashboard'],
    queryFn: async () => {
      const r = await apiClient.get('/firm/dashboard');
      return r.data?.data || r.data;
    },
    retry: 1,
    staleTime: 0,
  });

  const { data: billing } = useQuery({
    queryKey: ['billing'],
    queryFn: async () => { const r = await apiClient.get('/subscription/billing'); return r.data.data; },
  });

  const { data: alertsData } = useQuery({
    queryKey: ['manager-alerts'],
    queryFn: async () => { const r = await apiClient.get('/manager/alerts'); return r.data.data; },
    staleTime: 5 * 60 * 1000,
  });

  const stats = dashboard?.summary;
  const deadlines = dashboard?.upcomingDeadlines || [];
  const opportunities = dashboard?.opportunities || [];
  const activity = dashboard?.recentActivity || [];

  if (isLoading) return <SkeletonHomePage />;

  if (dashError) {
    return (
      <div className="px-5 py-6 max-w-lg mx-auto">
        <h1 className="text-2xl font-light mb-4">{greeting}, {firstName}</h1>
        <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-xl">
          <p className="text-sm text-red-400">Dashboard error: {(dashError as any)?.message || 'Unknown error'}</p>
          <p className="text-xs text-zinc-500 mt-2">Response: {JSON.stringify((dashError as any)?.response?.data)?.substring(0, 200)}</p>
        </div>
      </div>
    );
  }

  const urgentDeadlines = deadlines.filter((d: any) => {
    const days = Math.ceil((new Date(d.dueDate).getTime() - Date.now()) / 86400000);
    return days <= 7 && days >= 0;
  });
  const overdueDeadlines = deadlines.filter((d: any) => new Date(d.dueDate) < new Date());

  return (
    <div className="px-5 py-6 max-w-lg mx-auto space-y-7">
      {/* Greeting */}
      <div>
        <h1 className="text-3xl font-light">{greeting}, <span className="font-medium">{firstName}</span></h1>
        <p className="text-sm text-zinc-500 mt-1">
          {new Date().toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
        </p>
      </div>

      {/* Voice CTA */}
      <Link
        to="/pa/voice"
        className="flex items-center gap-4 p-5 bg-[#5048e5]/10 border border-[#5048e5]/20 rounded-2xl transition-all duration-300 hover:bg-[#5048e5]/15 group"
      >
        <div className="w-14 h-14 rounded-full bg-[#5048e5] flex items-center justify-center group-hover:shadow-[0_0_20px_rgba(80,72,229,0.4)] transition-all duration-300">
          <Mic className="h-6 w-6 text-white" />
        </div>
        <div className="flex-1">
          <p className="text-base font-medium">Ask me anything</p>
          <p className="text-sm text-zinc-500">"Which clients need attention today?"</p>
        </div>
        <ChevronRight className="h-5 w-5 text-zinc-600" />
      </Link>

      {/* Client Quick Cards - Horizontal Scroll */}
      {dashboard?.clients && dashboard.clients.length > 0 && (
        <div className="space-y-2">
          <h2 className="text-xs uppercase tracking-widest text-zinc-500 font-medium">Clients</h2>
          <div className="flex gap-3 overflow-x-auto pb-2 -mx-5 px-5 snap-x snap-mandatory scrollbar-hide" style={{ scrollbarWidth: 'none' }}>
            {dashboard.clients.slice(0, 15).map((c: any) => {
              const score = c.health_score || c.healthScore || 0;
              const scoreColor = score >= 70 ? 'text-green-400' : score >= 40 ? 'text-amber-400' : 'text-red-400';
              const initial = (c.display_name || c.name || '?')[0].toUpperCase();
              return (
                <Link
                  key={c.id}
                  to={`/pa/client/${c.id}`}
                  className="flex-shrink-0 w-32 snap-start bg-zinc-900/50 border border-white/[0.04] rounded-xl p-3 hover:border-[#5048e5]/20 transition active:scale-[0.97]"
                >
                  <div className="w-10 h-10 rounded-full bg-[#5048e5]/15 flex items-center justify-center text-sm font-bold text-[#7c75f0] mb-2">{initial}</div>
                  <p className="text-sm font-medium truncate">{c.display_name || c.name}</p>
                  {score > 0 && <p className={cn('text-xs mt-0.5', scoreColor)}>Health: {score}</p>}
                </Link>
              );
            })}
          </div>
        </div>
      )}

      {/* Morning Briefing / EOD Summary */}
      <div className="grid grid-cols-2 gap-3">
        <button
          onClick={() => handleBriefing('morning')}
          className="flex items-center gap-3 p-4 bg-amber-500/5 border border-amber-500/10 rounded-xl transition hover:bg-amber-500/10 active:scale-[0.98]"
        >
          <Sun className="h-5 w-5 text-amber-400" />
          <div className="text-left">
            <p className="text-sm font-medium">Morning Briefing</p>
            <p className="text-xs text-zinc-500">AI summary</p>
          </div>
        </button>
        <button
          onClick={() => handleBriefing('eod')}
          className="flex items-center gap-3 p-4 bg-indigo-500/5 border border-indigo-500/10 rounded-xl transition hover:bg-indigo-500/10 active:scale-[0.98]"
        >
          <Moon className="h-5 w-5 text-indigo-400" />
          <div className="text-left">
            <p className="text-sm font-medium">EOD Summary</p>
            <p className="text-xs text-zinc-500">Day wrap-up</p>
          </div>
        </button>
      </div>

      {/* Briefing Modal */}
      {showBriefing && (
        <div className="bg-zinc-900/60 border border-white/[0.06] rounded-2xl p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium flex items-center gap-2">
              {briefingType === 'morning' ? <Sun className="h-4 w-4 text-amber-400" /> : <Moon className="h-4 w-4 text-indigo-400" />}
              {briefingType === 'morning' ? 'Morning Briefing' : 'End of Day Summary'}
            </h3>
            <button onClick={() => { setShowBriefing(false); briefingMutation.reset(); }} className="text-zinc-500 hover:text-zinc-300"><X className="h-4 w-4" /></button>
          </div>

          {briefingMutation.isPending && (
            <div className="flex items-center gap-3 py-8 justify-center">
              <Loader2 className="h-5 w-5 animate-spin text-[#7c75f0]" />
              <span className="text-sm text-zinc-500">{briefingType === 'morning' ? 'Preparing your briefing...' : 'Summarising your day...'}</span>
            </div>
          )}

          {briefingMutation.isSuccess && briefingMutation.data && (
            <div className="space-y-3">
              {/* Stats pills */}
              {briefingMutation.data.stats && (
                <div className="flex flex-wrap gap-2">
                  {briefingMutation.data.stats.overdueDeadlines > 0 && (
                    <span className="text-[10px] bg-red-500/20 text-red-400 px-2 py-0.5 rounded-full">{briefingMutation.data.stats.overdueDeadlines} overdue</span>
                  )}
                  {briefingMutation.data.stats.criticalAlerts > 0 && (
                    <span className="text-[10px] bg-red-500/20 text-red-400 px-2 py-0.5 rounded-full">{briefingMutation.data.stats.criticalAlerts} critical</span>
                  )}
                  {briefingMutation.data.stats.unreadEmails > 0 && (
                    <span className="text-[10px] bg-[#5048e5]/20 text-[#7c75f0] px-2 py-0.5 rounded-full">{briefingMutation.data.stats.unreadEmails} unread emails</span>
                  )}
                  <span className="text-[10px] bg-green-500/20 text-green-400 px-2 py-0.5 rounded-full">{briefingMutation.data.stats.totalClients} clients</span>
                </div>
              )}
              {/* AI Briefing content */}
              <div className="text-sm text-zinc-300 leading-relaxed">
                <FormattedChatMessage content={briefingMutation.data.briefing} darkTheme />
              </div>
            </div>
          )}

          {briefingMutation.isError && (
            <div className="text-sm text-red-400 py-4 text-center">
              Failed to generate briefing. <button onClick={() => briefingMutation.mutate(briefingType)} className="underline">Try again</button>
            </div>
          )}
        </div>
      )}

      {/* Proactive Alerts */}
      {alertsData?.alerts && alertsData.alerts.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-xs uppercase tracking-widest text-zinc-500 font-medium flex items-center gap-1.5">
              <Bell className="h-3.5 w-3.5" /> Alerts
            </h2>
            {alertsData.summary?.critical > 0 && (
              <span className="text-xs bg-red-500/20 text-red-400 px-2 py-0.5 rounded-full">{alertsData.summary.critical} critical</span>
            )}
          </div>
          {alertsData.alerts.slice(0, 6).map((alert: any) => {
            const isCritical = alert.severity === 'critical';
            const isWarning = alert.severity === 'warning';
            const AlertIcon = alert.type === 'revenue_anomaly' ? TrendingDown
              : alert.type === 'at_risk' ? UserX
              : alert.type === 'super_overdue' ? ShieldAlert
              : alert.type === 'compliance_drift' ? AlertTriangle
              : AlertTriangle;

            return (
              <div
                key={alert.id}
                onClick={() => alert.clientId && navigate(`/pa/client/${alert.clientId}`)}
                className={cn(
                  'flex items-start gap-3 p-4 rounded-xl border transition',
                  isCritical ? 'bg-red-500/10 border-red-500/20' :
                  isWarning ? 'bg-amber-500/10 border-amber-500/20' :
                  'bg-zinc-900/30 border-white/[0.06]',
                  alert.clientId && 'cursor-pointer active:scale-[0.98]'
                )}
              >
                <AlertIcon className={cn('h-5 w-5 shrink-0 mt-0.5',
                  isCritical ? 'text-red-400' : isWarning ? 'text-amber-400' : 'text-zinc-500'
                )} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium">{alert.title}</p>
                  <p className={cn('text-sm mt-0.5 line-clamp-2 break-words',
                    isCritical ? 'text-red-400/80' : isWarning ? 'text-amber-400/80' : 'text-zinc-500'
                  )}>
                    {alert.description}
                  </p>
                  {alert.value != null && alert.type === 'revenue_anomaly' && (
                    <p className="text-xs text-red-400 mt-1 font-medium">{alert.value}% change</p>
                  )}
                  {alert.value != null && alert.type === 'super_overdue' && (
                    <p className="text-xs text-red-400 mt-1 font-medium">Est. shortfall: ${alert.value.toLocaleString()}</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Quick Stats */}
      <div className="grid grid-cols-2 gap-3">
        {[
          { icon: Users, color: 'text-[#7c75f0]', value: stats?.activeClients || stats?.totalClients || 0, label: 'Active Clients' },
          { icon: FileText, color: 'text-[#7c75f0]', value: stats?.totalDocuments || 0, label: 'Documents' },
          { icon: Clock, color: 'text-amber-400', value: stats?.xeroConnected || deadlines.length, label: 'Xero Connected' },
          { icon: DollarSign, color: 'text-green-400', value: opportunities.length, label: 'Opportunities' },
        ].map((s, i) => (
          <div key={i} className="bg-zinc-900/50 border border-white/[0.04] rounded-xl p-4">
            <s.icon className={cn('h-5 w-5 mb-2', s.color)} />
            <p className="text-3xl font-bold"><CountUp end={s.value} duration={800} /></p>
            <p className="text-sm text-zinc-500">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Tax Opportunities */}
      {opportunities.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-xs uppercase tracking-widest text-zinc-500 font-medium">Tax Opportunities Found</h2>
          {opportunities.slice(0, 3).map((opp: any, i: number) => (
            <div key={i} className="flex items-center gap-3 p-4 bg-green-500/5 border border-green-500/10 rounded-xl">
              <DollarSign className="h-5 w-5 text-green-400 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-base font-medium truncate">{opp.clientName}</p>
                <p className="text-sm text-zinc-500">{opp.description}</p>
              </div>
              <span className="text-base font-semibold text-green-400">{fmtCurrency(opp.estimatedSaving || 0)}</span>
            </div>
          ))}
        </div>
      )}

      {/* Recent Activity */}
      {activity.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-xs uppercase tracking-widest text-zinc-500 font-medium">Recent Activity</h2>
          {activity.slice(0, 5).map((a: any, i: number) => (
            <div key={i} className="flex items-center gap-3 py-3 border-b border-white/[0.04] last:border-0">
              <div className="w-2 h-2 rounded-full bg-zinc-700" />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-zinc-400 truncate">{a.description} {a.client_name && `- ${a.client_name}`}</p>
              </div>
              <span className="text-xs text-zinc-600 shrink-0">
                {new Date(a.event_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Plan Info */}
      {billing?.plan && (
        <div className="text-center pt-4">
          <p className="text-xs text-zinc-700">
            {billing.plan.displayName || billing.plan.planName} Plan
            {billing.usage?.aiQueries && ` - ${billing.usage.aiQueries.used}/${billing.usage.aiQueries.max} queries`}
          </p>
        </div>
      )}
    </div>
  );
}
